import { useForm, useFieldArray } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useState, useEffect, useRef } from 'react';
import type { InitCompanyDto } from '@monorepo/shared';
import { UserCompanyRole, CompanyLogoType } from '@monorepo/shared';
import { useInitCompany, companyApi } from '../services/company.service';
import { useUserCompaniesStore } from '../stores/userCompanies.store';
import { useSelectedCompanyStore } from '../stores/selectedCompany.store';
import { useAuthStore } from '../stores/auth.store';
import { type PendingCompanyLogoSectionRef } from '../components/company/PendingCompanyLogoSection';
import { CompanySettingsFormView } from '../components/company/CompanySettingsFormView';
import { queryClient } from '../lib/queryClient';

export const CompanySettingsPage = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [errorMessage, setErrorMessage] = useState<string>('');
  const user = useAuthStore((state) => state.user);
  const companies = useUserCompaniesStore((state) => state.companies);
  const setCompanies = useUserCompaniesStore((state) => state.setCompanies);
  const setSelectedCompany = useSelectedCompanyStore((state) => state.setSelectedCompany);

  useEffect(() => {
    if (user?.hasCompany && !user?.isMainContact) {
      navigate('/');
    }
  }, [user, navigate]);

  const {
    register,
    handleSubmit,
    watch,
    control,
    setValue,
    getValues,
    formState: { errors },
  } = useForm<InitCompanyDto>({
    defaultValues: {
      isMainContact: true,
      locations: [],
      dataProtectionSettings: {
        hasDpoDeclaration: false,
        hasDataProtectionRepresentative: false,
        isExternalConsultant: false,
      },
      hasVideoSurveillance: false,
      hasVideoProtection: false,
      recordsPhoneCalls: false,
      hasWorksCouncil: false,
      hasLegalDepartment: false,
      hasItDepartment: false,
    },
  });

  const locationsFieldArray = useFieldArray({
    control,
    name: 'locations',
  });

  const logoRef = useRef<PendingCompanyLogoSectionRef>(null);
  const initCompanyMutation = useInitCompany();
  const isMainContact = watch('isMainContact');
  const hasDpoDeclaration = watch('dataProtectionSettings.hasDpoDeclaration');
  const hasDataProtectionRepresentative = watch(
    'dataProtectionSettings.hasDataProtectionRepresentative',
  );
  const isExternalConsultant = watch('dataProtectionSettings.isExternalConsultant');
  const siren = watch('siren');
  const industrySector = watch('industrySector');

  const onSubmit = async (data: InitCompanyDto) => {
    setErrorMessage('');
    try {
      const submitData = {
        ...data,
        locations: [
          {
            siret: (data as any).mainLocation.siret,
            address: (data as any).mainLocation.address,
            isMainCompanyLocation: true,
          },
          ...(data.locations || []),
        ],
      };

      const newCompany = await initCompanyMutation.mutateAsync(submitData);

      const pendingLogos = logoRef.current?.getFiles();
      if (pendingLogos?.full) {
        await companyApi.uploadCompanyLogo(newCompany.id, CompanyLogoType.FULL, pendingLogos.full);
      }
      if (pendingLogos?.square) {
        await companyApi.uploadCompanyLogo(
          newCompany.id,
          CompanyLogoType.SQUARE,
          pendingLogos.square,
        );
      }

      const newCompanyDto = {
        id: newCompany.id,
        siren: newCompany.siren,
        legalName: newCompany.legalName,
        isMainContact: data.isMainContact,
        role: UserCompanyRole.SUPER_ADMIN,
      };

      const updatedCompanies = [...companies, newCompanyDto];
      setCompanies(updatedCompanies);
      setSelectedCompany(newCompany.id);

      await queryClient.invalidateQueries({ queryKey: ['profile'] });

      navigate('/');
    } catch (error: any) {
      console.error('Company initialization failed:', error);
      const errorMsg = error?.response?.data?.message;
      if (errorMsg === 'SIREN_ALREADY_EXISTS') {
        setErrorMessage(t('company.error.siren_already_exists'));
      } else if (errorMsg === 'SIRET_ALREADY_EXISTS') {
        setErrorMessage(t('company.error.siret_already_exists'));
      } else {
        setErrorMessage(t('company.error.submit_failed'));
      }
    }
  };

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <div className="bg-white rounded-lg shadow-lg overflow-hidden">
        <div className="p-6 border-b border-gray-200">
          <h1 className="text-3xl font-display text-primary mb-2">{t('company.settings.title')}</h1>
          <p className="text-secondary">{t('company.settings.subtitle')}</p>
        </div>

        <div className="max-h-[calc(100vh-16rem)] overflow-y-auto p-6">
          <CompanySettingsFormView
            register={register}
            errors={errors}
            setValue={setValue}
            getValues={getValues}
            locationsFieldArray={locationsFieldArray}
            logoRef={logoRef}
            isMainContact={isMainContact}
            hasDpoDeclaration={hasDpoDeclaration || false}
            hasDataProtectionRepresentative={hasDataProtectionRepresentative || false}
            isExternalConsultant={isExternalConsultant || false}
            siren={siren}
            industrySector={industrySector}
            errorMessage={errorMessage}
            isSubmitting={initCompanyMutation.isPending}
            onSubmit={handleSubmit(onSubmit)}
          />
        </div>
      </div>
    </div>
  );
};
